'use client';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
/** The keys that move focus, mapped to a step. `null` = not a navigation key. */
function stepFor(key, dir) {
    const forward = dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
    const back = dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft';
    if (key === forward || key === 'ArrowDown')
        return 1;
    if (key === back || key === 'ArrowUp')
        return -1;
    if (key === 'Home')
        return 'first';
    if (key === 'End')
        return 'last';
    return null;
}
/** Index of the next enabled option, wrapping around; -1 when none is enabled. */
function nextEnabled(options, from, step, allDisabled) {
    if (allDisabled)
        return -1;
    const n = options.length;
    if (step === 'first')
        return options.findIndex((o) => !o.disabled);
    if (step === 'last') {
        for (let i = n - 1; i >= 0; i--)
            if (!options[i]?.disabled)
                return i;
        return -1;
    }
    for (let k = 1; k <= n; k++) {
        const i = (((from + step * k) % n) + n) % n;
        if (!options[i]?.disabled)
            return i;
    }
    return -1;
}
function directionOf(el, dir) {
    if (dir)
        return dir;
    return typeof getComputedStyle === 'function' && getComputedStyle(el).direction === 'rtl'
        ? 'rtl'
        : 'ltr';
}
function focusOption(root, index) {
    root?.querySelectorAll('[data-index]')[index]?.focus();
}
function OptionContent({ option }) {
    return (_jsxs(_Fragment, { children: [option.icon ? (_jsx("span", { className: "mn-segmented-control__icon", "aria-hidden": true, children: option.icon })) : null, _jsx("span", { className: option.hideLabel ? 'mn-visually-hidden' : 'mn-segmented-control__label', children: option.label }), option.count !== undefined ? (_jsxs(_Fragment, { children: [' ', _jsx("span", { className: "mn-segmented-control__count", children: option.count })] })) : null, option.suffix ? (_jsxs(_Fragment, { children: [' ', _jsx("span", { className: "mn-segmented-control__suffix", children: option.suffix })] })) : null] }));
}
function useControllable(value, defaultValue, onChange) {
    const [inner, setInner] = useState(defaultValue);
    const controlled = value !== undefined;
    const current = controlled ? value : inner;
    const set = useCallback((next) => {
        if (!controlled)
            setInner(next);
        onChange?.(next);
    }, [controlled, onChange]);
    return [current, set];
}
/**
 * A single choice from 2–5 short options, shown as one connected control:
 * view switches (list/board), periods (monthly/yearly), filters. It is a
 * radio group (WAI-ARIA APG): one Tab stop, arrow keys move and select,
 * Home/End jump. For several independent on/off choices use ToggleGroup;
 * when each option shows its own panel use Tabs.
 */
function SegmentedControlInner({ options, value: valueProp, defaultValue, onValueChange, size = 'md', block = false, disabled = false, dir, name, required, className, ...rest }, ref) {
    const [value, setValue] = useControllable(valueProp, defaultValue, onValueChange);
    const selectedIndex = options.findIndex((o) => o.value === value);
    // The one Tab stop: the selected option, or the first enabled one.
    const tabStop = selectedIndex >= 0 && !options[selectedIndex]?.disabled
        ? selectedIndex
        : nextEnabled(options, 0, 'first', disabled);
    const handleKeyDown = (event, from) => {
        const step = stepFor(event.key, directionOf(event.currentTarget, dir));
        if (step === null)
            return;
        const to = nextEnabled(options, from, step, disabled);
        const option = options[to];
        if (!option)
            return;
        event.preventDefault();
        focusOption(event.currentTarget.parentElement, to);
        // Radio pattern: selection follows focus.
        if (option.value !== value)
            setValue(option.value);
    };
    return (_jsxs("div", { ref: ref, role: "radiogroup", ...rest, dir: dir, "aria-disabled": disabled || undefined, "aria-required": required || undefined, className: cx('mn-segmented-control', className), "data-size": size, "data-block": block || undefined, "data-disabled": disabled || undefined, children: [options.map((option, index) => {
                const checked = index === selectedIndex;
                const off = disabled || option.disabled;
                return (_jsx("button", { type: "button", role: "radio", "aria-checked": checked, tabIndex: index === tabStop ? 0 : -1, disabled: off || undefined, title: option.hideLabel ? option.label : undefined, "data-index": index, "data-state": checked ? 'on' : 'off', "data-icon-only": option.hideLabel || undefined, className: "mn-segmented-control__item", onKeyDown: (event) => handleKeyDown(event, index), onClick: () => {
                        if (!checked)
                            setValue(option.value);
                    }, children: _jsx(OptionContent, { option: option }) }, option.value));
            }), name !== undefined ? _jsx("input", { type: "hidden", name: name, value: value ?? '' }) : null] }));
}
export const SegmentedControl = forwardRef(SegmentedControlInner);
/**
 * Several independent on/off choices shown as one connected control, for
 * example a set of filters. Each option is a toggle button (`aria-pressed`)
 * inside a labelled group. One Tab stop; arrow keys move focus, Space or
 * Enter toggles.
 */
function ToggleGroupInner({ options, value: valueProp, defaultValue, onValueChange, size = 'md', block = false, disabled = false, dir, className, ...rest }, ref) {
    const [value, setValue] = useControllable(valueProp, defaultValue ?? [], onValueChange);
    // Roving focus remembers the last focused option, independent of what is pressed.
    const [active, setActive] = useState(() => Math.max(0, nextEnabled(options, 0, 'first', disabled)));
    const tabStop = options[active] && !options[active]?.disabled
        ? active
        : nextEnabled(options, 0, 'first', disabled);
    const handleKeyDown = (event, from) => {
        const step = stepFor(event.key, directionOf(event.currentTarget, dir));
        if (step === null)
            return;
        const to = nextEnabled(options, from, step, disabled);
        if (to < 0)
            return;
        event.preventDefault();
        setActive(to);
        focusOption(event.currentTarget.parentElement, to);
    };
    return (_jsx("div", { ref: ref, role: "group", ...rest, dir: dir, "aria-disabled": disabled || undefined, className: cx('mn-segmented-control', className), "data-size": size, "data-block": block || undefined, "data-disabled": disabled || undefined, "data-multiple": "", children: options.map((option, index) => {
            const pressed = value.includes(option.value);
            const off = disabled || option.disabled;
            return (_jsx("button", { type: "button", "aria-pressed": pressed, tabIndex: index === tabStop ? 0 : -1, disabled: off || undefined, title: option.hideLabel ? option.label : undefined, "data-index": index, "data-state": pressed ? 'on' : 'off', "data-icon-only": option.hideLabel || undefined, className: "mn-segmented-control__item", onFocus: () => setActive(index), onKeyDown: (event) => handleKeyDown(event, index), onClick: () => {
                    const next = pressed
                        ? value.filter((v) => v !== option.value)
                        : options
                            .filter((o) => o.value === option.value || value.includes(o.value))
                            .map((o) => o.value);
                    setValue(next);
                }, children: _jsx(OptionContent, { option: option }) }, option.value));
        }) }));
}
export const ToggleGroup = forwardRef(ToggleGroupInner);
//# sourceMappingURL=segmented-control.js.map